import qs from 'qs'
import { required, email } from '../utils/validators'
import parseNumber from '../utils/parse-number'

export const state = () => {
  return {
    id: null,
    products: [],
    selectedProductId: null,
    selectedGateway: '',
    showModal: false,
    showLogin: false,
    // default labels, can be updated through SET_STEPS mutation
    stepLabels: ['Abonnement', 'Gegevens', 'Betaling'],
    currentStep: 0,
    combiDiscounts: [],
    discounts: [],
    submitAttempt: false,
    apiErrors: {}
  }
}

export const mutations = {
  SET_ID (state, data) {
    state.id = data
  },
  SET_MODAL (state, data) {
    state.showModal = data
  },
  SET_LOGIN (state, data) {
    state.showLogin = data
  },
  SET_PRODUCTS (state, data) {
    state.products = data
  },
  SET_SELECTED_PRODUCT_ID (state, data) {
    state.selectedProductId = data
  },
  SET_SELECTED_GATEWAY (state, data) {
    state.selectedGateway = data
  },
  SET_STEP_LABELS (state, data) {
    state.stepLabels = data
  },
  GOTO_STEP (state, num) {
    state.currentStep = num
  },
  SET_SUBMIT_ATTEMPT (state, data) {
    state.submitAttempt = data
  },
  SET_API_ERRORS (state, data) {
    state.apiErrors = data
  },
  SET_COMBI_DISCOUNTS (state, data) {
    state.combiDiscounts = data
  },
  SET_DISCOUNTS (state, data) {
    state.discounts = data
  }
}

export const getters = {
  hasProducts: (state) => {
    return state.products.length > 0
  },
  sortedProducts: (state, getters) => {
    if (!getters.hasProducts) return []
    const periodInMonths = (product) =>
      product.period_type && product.period_type === 'Year' ? product.period * 12 : product.period
    // sorts by period, e.g 1, 6, 12
    return [...state.products].sort((a,b) => {
      return periodInMonths(a) > periodInMonths(b) ? 1 : -1
    })
  },
  productDurations: (state) => {
    return state.products
      .filter(product => !!product.period)
      .map(product => {
        const { period, period_type } = product
        return { period, period_type }
    })
  },
  productDurationsLabels: (state, getters) => {
    if (getters.productDurations.length === 0) return {}
    const translations = { 
      month: { single: 'maand', plural: 'maanden' },
      year: { single: 'jaar', plural: 'jaren' }
    }
    const pluralize = (type, length) => {
      const duration = translations[type.toLowerCase()]
      return length > 1 ? duration.plural : duration.single
    }
    return getters.productDurations.reduce((acc, curr) => {
      acc[curr.period] = `${curr.period} ${pluralize(curr.period_type, curr.period)}`
      return acc
    }, {})
  },
  showDurationOptions: (state) => {
    return state.products.length > 1
  },
  selectedProduct: (state, getters) => {
    return state.products.find(product => product.id === state.selectedProductId) || getters.sortedProducts[getters.sortedProducts.length - 1]
  },
  selectedProductType: (state, getters) => {
    // only used for submitting the proper order key
    const typesMap = {
      'Subscription': 'subscription_types',
      'Ebook': 'ebooks',
      'Ebook Bundle': 'ebook-bundles'
    }
    const type = getters.selectedProduct.type
    return typesMap[type] || 'subscription_types'
  },
  selectedProductTypeForDiscount: (state, getters) => {
    const typesMap = {
      'Subscription': 'subscriptionTypes',
      'Ebook': 'ebooks',
      'Ebook Bundle': 'ebookBundles'
    }
    const type = getters.selectedProduct.type
    return typesMap[type] || 'subscriptionTypes'
  },
  selectedDuration: (state, getters) => {
    return getters.selectedProduct.period || false
  },
  selectedProductCombiDiscount: (state, getters) => {
    return getters.parsedCombiDiscounts.find(discount => discount.subscriptionTypes.includes(getters.selectedProduct.id)) || false
  },
  gateways: (state, getters) => {
    if (!getters.selectedProduct) return []
    return getters.selectedProduct.gateways.flat()
  },
  steps: (state, getters) => {
    let labels = [...state.stepLabels]
    if (!getters.showDurationOptions) labels.shift()
    return labels
  },
  totalPrice: (state, getters) => {
    if (!getters.hasProducts) return 0
    return getters.selectedProduct.total_price.value
  },
  totalPriceAfterDiscount: (state, getters) => {
    if (!getters.hasProducts) return 0
    return getters.totalPrice - getters.discountValue
  },
  periodPrices: (state, getters) => {
    if (!getters.hasProducts) return 0
    return state.products
      .filter(product => !!product.period)
      .reduce((acc, curr) => {
        acc[curr.period] = curr.period_price.value
        return acc
      }, {})
  },
  periodPricesAfterDiscount: (state, getters) => {
    return state.products.filter(product => !!product.period).reduce((periodPrices, product) => {
      const discount = state.discounts.find(discount => discount[getters.selectedProductTypeForDiscount].includes(product.id)) || { value: 0 }
      let discountValue = discount.type === 'percentage'
        ? product.total_price.value * parseFloat(discount.value / 100)
        : parseFloat(discount.value)
      const combiDiscount = getters.parsedCombiDiscounts.find(discount => discount[getters.selectedProductTypeForDiscount].includes(product.id)) || false
      if (combiDiscount && combiDiscount.value > discountValue) {
        discountValue = combiDiscount.value
      }
      periodPrices[product.period] = (product.total_price.value - discountValue) / product.period
      return periodPrices
    }, {})
  },
  $error: (state, getters) => {
    for (let key in getters.validations) {
      if (getters.validations[key].$error) return true
    }
    return false
  },
  validations: (state, getters, rootState) => {
    const results = {}
    const fields = {
      selectedGateway: { required },
      first_name: { required },
      last_name: { required },
      email: { required, email },
      emailRepeat: { required, email }
    }
    for (let field in fields) {
      results[field] = {}
      const isUserField = ['first_name', 'last_name', 'email', 'emailRepeat'].includes(field)
      for (let validator in fields[field]) {
        // hacky way because of data structure, improve this in the future
        const fieldValue = isUserField ? rootState.auth.user[field] : state[field]
        results[field][validator] =  fields[field][validator](fieldValue)
        results[field].$invalid = !results[field][validator]
        results[field].$error = state.submitAttempt && results[field].$invalid
      }
    }
    results.emailRepeat.sameAs = !results.emailRepeat.$invalid && rootState.auth.user.email === rootState.auth.user.emailRepeat
    results.emailRepeat.$invalid = results.emailRepeat.$invalid || !results.emailRepeat.sameAs
    results.emailRepeat.$error = state.submitAttempt && results.emailRepeat.$invalid
    return results
  },
  personalDetailsError (state, getters) {
    const fields = ['first_name', 'last_name', 'email', 'emailRepeat']
    return fields.some((field) => {
      return getters.validations[field].$error || getters.validations[field].$invalid
    })
  },
  parsedCombiDiscounts: (state) => {
    return [...state.combiDiscounts].map((discount) => {
      const arr = discount.code.split('-')
      const updatedDiscount = Object.assign({...discount}, {
        name: arr.length === 2 ? arr[0] : arr.splice(0, arr.length - 1).join('-'),
        period: parseInt(arr.slice(-1)),
        value: parseNumber(discount.value)
      })
      return updatedDiscount
    })
  },
  discount: (state, getters) => {
    let discount = false
    discount = state.discounts.find(discount => discount[getters.selectedProductTypeForDiscount].includes(getters.selectedProduct.id)) || false
    if (discount && discount.type === 'percentage') {
      const discountValue = getters.selectedProduct.total_price.value * parseFloat(discount.value / 100)
      discount = { code: discount.code, value: discountValue, percentage: discount.value }
    }
    // 0 - discount if it's bigger than combi discount
    // 1 - combidiscount
    // 2 - discount (can be false)
    if (discount && discount.value) {
      // hotfix
      discount.value = parseNumber(discount.value)
      if (discount.value > getters.selectedProductCombiDiscount.value) {
        return discount
      }
    }
    if (getters.selectedProductCombiDiscount) {
      return getters.selectedProductCombiDiscount
    }
    return discount
  },
  discountValue: (state, getters) => {
    return getters.discount.value || 0
  }
}

export const actions = {
  async submitOrder ({ state, commit, getters, rootState }) {
    commit('SET_API_ERRORS', {})
    const { first_name, last_name, email } = rootState.auth.user
    const order = {
      first_name,
      last_name,
      email,
      [getters.selectedProductType]: [getters.selectedProduct.id],
      gateway: state.selectedGateway,
      discount: getters.discount.code,
      callback: window.location.href.split(/[?#]/)[0] + '?id=' + state.id
    }
    try {
      const { data } = await this.$axiosCheckout.post('/submit-order', qs.stringify(order))
      return data
    } catch(e) {
      if (e.response && e.response.data) {
        commit('SET_API_ERRORS', e.response.data)
        throw(e)
      }
    }
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}